import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AppProvider, useAppContext } from './contexts/AppContext';
import { initAnalytics } from './utils/analytics';
import { useAuthRedirect } from './hooks/useAuthRedirect';
import { useScrollToHash } from './hooks/useScrollToHash';
import Navbar from './components/Navbar';
import LandingPage from './pages/LandingPage';
import RSVPReader, { RampConfig } from './components/RSVPReader';
import ProfilePage from './pages/ProfilePage';
import StoriesPage from './pages/StoriesPage';
import LibraryPage from './pages/LibraryPage';
import LoginPage from './pages/LoginPage';
import SignupPage from './pages/SignupPage';
import UpsellPage from './pages/UpsellPage';
import UpsellPageOld from './pages/UpsellPageOld';
import WhyPage from './pages/WhyPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import AchievementsPage from './pages/AchievementsPage';
import TermsPage from './pages/TermsPage';
import PrivacyPage from './pages/PrivacyPage';
import ResearchPage from './pages/ResearchPage';
import { OnboardingTour } from './components/onboarding';
import AchievementUnlockModal from './components/AchievementUnlockModal';
import { CookieConsent } from './components/CookieConsent';
import Footer from './components/Footer';

// ============================================================================
// Error Boundary Component
// ============================================================================
export class ErrorBoundary extends React.Component {
  state = { hasError: false };

  static getDerivedStateFromError(error: Error) {
    return { hasError: true };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('Error caught by boundary:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-red-50 dark:bg-red-900">
          <div className="text-center p-8">
            <h2 className="text-2xl font-bold text-red-600 dark:text-red-400 mb-4">
              Something went wrong
            </h2>
            <p className="text-gray-600 dark:text-gray-400">
              We're sorry, but something unexpected happened. Please try refreshing the page.
            </p>
          </div>
        </div>
      );
    }

    return (this.props as any).children;
  }
}

// ============================================================================
// Auth Guard Component — redirects paid-tier users away from login/signup
// ============================================================================
function AuthGuard({ children }: { children: React.ReactNode }) {
  useAuthRedirect();
  return <>{children}</>;
}

// ============================================================================
// Reader Guard — redirects to /stories if no reading content
// Checks both context state AND location.state to avoid race condition
// with handleStartReading setting state + navigating in the same tick.
// ============================================================================
function ReaderGuard({ children }: { children: React.ReactNode }) {
  const { readingContent } = useAppContext();
  const location = useLocation();
  const locationContent = (location.state as any)?.readingContent;

  // Allow through if context has content OR if navigation state has content
  // (location.state is set by handleStartReading to avoid the race condition)
  if (!readingContent && !locationContent) {
    return <Navigate to="/stories" replace />;
  }
  return <>{children}</>;
}

// ============================================================================
// Signup Page with hash scroll support
// ============================================================================
function SignupPageWithScroll() {
  useScrollToHash();
  return <SignupPage />;
}

// ============================================================================
// App Content (inside Router, uses context)
// ============================================================================
function AppContent() {
  const {
    isAuthenticated,
    userTier,
    theme,
    toggleTheme,
    wpm,
    setWpm,
    stats,
    readingContent,
    readingSections,
    readingCategoryId,
    currentBookId,
    currentBookTitle,
    initialWordIndex,
    readingFormat,
    handleSessionComplete,
    handleStartReading,
    handleLogout,
    handleOnboardingSignup,
    showTour,
    setShowTour,
    setHasSeenTour,
    unlockedAchievement,
    setUnlockedAchievement,
  } = useAppContext();

  const effectiveAuth = isAuthenticated;

  // Default ramp config for the reader
  const defaultRampConfig: RampConfig = {
    enabled: true,
    startWpm: 200,
    endWpm: 500,
    durationSeconds: 60
  };

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-950 font-sans text-slate-900 dark:text-slate-100 transition-colors duration-300">
      <Navbar
        theme={theme}
        onToggleTheme={toggleTheme}
        isAuthenticated={effectiveAuth}
        onLogout={handleLogout}
        membershipTier={userTier}
      />

      {/* Onboarding Tour */}
      {showTour && (
        <OnboardingTour
          onComplete={() => {
            setShowTour(false);
            setHasSeenTour(true);
          }}
          onSkip={() => {
            setShowTour(false);
            setHasSeenTour(true);
          }}
          onSignup={handleOnboardingSignup}
        />
      )}

      {/* Achievement Unlock Modal */}
      <AchievementUnlockModal
        achievement={unlockedAchievement}
        onClose={() => setUnlockedAchievement(null)}
      />

      <main className="flex-1 pb-16">
        <Routes>
          <Route path="/" element={<LandingPage />} />
          <Route path="/stories" element={
            <StoriesPage
              onStartReading={handleStartReading}
              userTier={userTier}
            />
          } />
          <Route path="/profile" element={
            <ProfilePage
              stats={stats}
              currentWpm={wpm}
              userTier={userTier}
            />
          } />
          <Route path="/library" element={
            <LibraryPage
              stats={stats}
              currentWpm={wpm}
              initialContent={readingContent}
              onStartReading={handleStartReading}
              userTier={userTier}
            />
          } />
          <Route path="/achievements" element={
            <AchievementsPage
              stats={stats}
              userTier={userTier}
            />
          } />
          <Route path="/login" element={
            <AuthGuard>
              <LoginPage />
            </AuthGuard>
          } />
          <Route path="/signup" element={
            <AuthGuard>
              <SignupPageWithScroll />
            </AuthGuard>
          } />
          <Route path="/why" element={<WhyPage />} />
          <Route path="/research" element={<ResearchPage />} />
          <Route path="/terms" element={<TermsPage />} />
          <Route path="/privacy" element={<PrivacyPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route path="/upsell" element={<UpsellPage />} />
          <Route path="/upsell-old" element={<UpsellPageOld />} />
          <Route path="/reader" element={
            <ReaderGuard>
              <RSVPReader
                content={readingContent}
                sections={readingSections}
                categoryId={readingCategoryId}
                initialWpm={wpm}
                onWpmChange={setWpm}
                onComplete={handleSessionComplete}
                rampConfig={defaultRampConfig}
                isAuthenticated={effectiveAuth}
                userTier={userTier}
                autoStart={false}
                guestSessionsRemaining={effectiveAuth ? undefined : (() => {
                  const oldKey = parseInt(localStorage.getItem('flashread_guest_sessions') || '0', 10);
                  const newKey = parseInt(localStorage.getItem('flashread_free_stories_used') || '0', 10);
                  const used = Math.max(oldKey, newKey);
                  return Math.max(0, 3 - used);
                })()}
                bookId={currentBookId}
                bookTitle={currentBookTitle}
                initialWordIndex={initialWordIndex}
                format={readingFormat}
              />
            </ReaderGuard>
          } />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </main>

      <Footer />
      <CookieConsent />
    </div>
  );
}

// ============================================================================
// Root App Component
// ============================================================================
function App() {
  return (
    <ErrorBoundary>
      <Router>
        <AppProvider>
          <AppContent />
        </AppProvider>
      </Router>
    </ErrorBoundary>
  );
}

// Register the site-wide Google Analytics (GA4) tag. It is consent-gated via
// the ScriptManager, so it loads on every route only after analytics consent.
initAnalytics();

export default App;
